import { flowRight, get } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Interpolate } from 'react-i18next';
import classNames from 'classnames';
import { EXPERIMENT_SWITCH_NOTIFICATIONS_SERVICE } from '../../constants/experiments';
import Link from '../../components/link/internal-link';
import UserAvatarImage from '../user-avatar-image';
import TimeAgo from '../time-ago';
import withFontClassName from '../../hoc/with-font-class-name';
import { REASONS } from '../report-abuse-form/report-abuse-form';
import { isFullUrl } from '../../services/is-full-url';
import getOuterUrl from '../../services/get-outer-url';
import withExperiment from '../../hoc/with-experiment';
import withTranslate from '../../hoc/with-translate';
import styles from './notification-list-item.scss';

const buildInternalTo = ({ url, commentId }) => `${url}${commentId ? `#${commentId}` : ''}`;
const buildExternalTo = ({ url }) => isFullUrl(url) ? url : getOuterUrl(url);

const getReportMessage = notification =>
  get(REASONS.find(reason => reason.value && reason.value.toString() === notification.message), 'text');

const trimPostTitle = title => (title.length <= 20 ? title : `${title.substring(0, 20)}...`);

const getPostTitle = notification =>
  (notification.data && notification.data.title && trimPostTitle(notification.data.title)) || 'a post';

const renderMessage = (notification, t) => {
  switch (notification.type) {
    case 'comment':
      return (
        <Interpolate
          i18nKey="notification-list-item.comment"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
          message={<strong>
            {t('notification-list-item.message', { message: notification.message })}
          </strong>}
        />
      );
    case 'post-like':
      return (
        <Interpolate
          i18nKey="notification-list-item.post-liked"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
          message={<strong>
            {t('notification-list-item.message', { message: notification.message })}
          </strong>}
        />
      );
    case 'comment-like':
      return (
        <Interpolate
          i18nKey="notification-list-item.comment-liked"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
          message={<strong>
            {t('notification-list-item.message', { message: notification.message })}
          </strong>}
        />
      );
    case 'post-report':
      return (
        <Interpolate
          i18nKey="notification-list-item.post-reported"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
          title={t('notification-list-item.title', { title: getPostTitle(notification) })}
          message={<strong>
            {t('notification-list-item.message', { message: t(getReportMessage(notification) || '') })}
          </strong>}
        />
      );
    case 'comment-report':
      return (
        <Interpolate
          i18nKey="notification-list-item.comment-reported"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
          title={t('notification-list-item.title', { title: getPostTitle(notification) })}
          message={<strong>
            {t('notification-list-item.message', { message: t(getReportMessage(notification) || '') })}
          </strong>}
        />
      );
    case 'user-report':
      return (
        <Interpolate
          i18nKey="notification-list-item.user-reported"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
          reportedName={notification.data.name}
          message={<strong>
            {t('notification-list-item.message', { message: t(getReportMessage(notification) || '') })}
          </strong>}
        />
      );
    case 'post':
      return (
        <Interpolate
          i18nKey="notification-list-item.post"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
          message={<strong>
            {t('notification-list-item.message', { message: notification.message })}
          </strong>}
        />
      );
    case 'permissions-moderator-promote':
      return (
        <Interpolate
          i18nKey="notification-list-item.permissions-moderator-promoted"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
        />
      );
    case 'permissions-moderator-demote':
      return (
        <Interpolate
          i18nKey="notification-list-item.permissions-moderator-demoted"
          useDangerouslySetInnerHTML={true}
          name={<strong>
            {t('notification-list-item.name', { name: notification.user.name })}
          </strong>}
        />
      );

    default:
      return <Interpolate i18nKey="notification-list-item.unknown" useDangerouslySetInnerHTML={true}/>;
  }
};

export class NotificationListItem extends Component {
  handleClick = () => {
    const { onClick, notification } = this.props;
    onClick(notification);
  };

  render() {
    const { notification, contentFontClassName, t, useInternalRouting } = this.props;
    const linkProps = {
      to: useInternalRouting ? buildInternalTo(notification) : buildExternalTo(notification),
      fullUrl: !useInternalRouting,
    };
    return (
      <Link onClick={this.handleClick} {...linkProps}>
        <div
          className={classNames(styles.container, contentFontClassName, 'forum-text-color', 'notification-list-item')}
          data-hook="notification-list-item"
        >
          <div className={styles.column1}>
            <UserAvatarImage className={styles.avatar} user={notification.user}/>
          </div>
          <div className={styles.column2}>
            <div
              className={classNames(styles.message, 'notification-list-item__message')}
              data-hook="notification-list-item__message"
            >
              {renderMessage(notification, t)}
            </div>
            <TimeAgo className={styles.timeAgo} time={notification.createdDate}/>
          </div>
          <div className={styles.column3}>
            {!notification.isRead ?
              <span
                className={classNames(styles.dot, 'icon-secondary-background-color', 'notification-list-item__dot')}
              /> :
              null}
          </div>
        </div>
      </Link>
    );
  }
}

NotificationListItem.propTypes = {
  t: PropTypes.func.isRequired,
  notification: PropTypes.object.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  useInternalRouting: PropTypes.bool,
};


export default flowRight(
  withExperiment({ useInternalRouting: EXPERIMENT_SWITCH_NOTIFICATIONS_SERVICE }),
  withFontClassName,
  withTranslate
)(NotificationListItem);



// WEBPACK FOOTER //
// ./app/components/notification-list-item/notification-list-item.js